import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import api from 'services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api' 
    : 'http://localhost:5000/api'
);

const BugReportButton: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await api.post(
        `${API_BASE_URL}/bug-reports`,
        {
          page: location.pathname,
          description: description
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setShowSuccess(true);
      setDescription('');
      setTimeout(() => {
        setShowSuccess(false);
        setIsOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      alert(t('bugReports.failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-14 right-4 z-50">
      {!isOpen && !showSuccess && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg transition-colors"
          title={t('bugReports.reportABug')}
        >
          🐛 {t('bugReports.reportIssue')}
        </button>
      )}

      {isOpen && (
        <div className="bg-white rounded-lg shadow-xl p-4 w-80 border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800">{t('bugReports.reportAnIssue')}</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">
                {t('bugReports.page')}: <span className="font-medium">{location.pathname}</span>
              </label>
            </div>

            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">
                {t('bugReports.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={4}
                placeholder={t('bugReports.describeIssue')}
                required
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('bugReports.sending') : t('bugReports.sendReport')}
            </button>
          </form>
        </div>
      )}

      {showSuccess && (
        <div className="bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center">
            <span className="mr-2">✓</span>
            <span>{t('bugReports.success')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugReportButton;
